import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Smartphone,
  Terminal,
  Radio,
  ShieldAlert,
  Send,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card.js';
import { DeviceStatusBadge, CommandStatusBadge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { CopyButton } from '../../components/ui/CopyButton.js';
import { Modal, ConfirmDialog } from '../../components/ui/Modal.js';
import { CardSkeleton, TableSkeleton } from '../../components/ui/Skeleton.js';
import { ErrorState } from '../../components/ui/EmptyState.js';
import { PermissionGuard } from '../../components/security/PermissionGuard.js';
import { useToast } from '../../context/ToastContext.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchDeviceById, revokeDevice } from '../../api/devices.js';
import { createDeviceCommand, fetchCommands } from '../../api/commands.js';
import { COMMAND_TYPE_LABELS } from '../../lib/constants.js';
import { formatDate, formatRelativeTime, truncateId } from '../../lib/utils.js';
import type { DeviceCommandType } from '@hisob/types';

export function DeviceDetailPage() {
  const { deviceId } = useParams<{ deviceId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { success, error: toastError } = useToast();
  const { t } = useI18n();

  const [isCommandModalOpen, setIsCommandModalOpen] = useState(false);
  const [selectedCommandType, setSelectedCommandType] = useState<DeviceCommandType>('diagnostics');
  const [commandReason, setCommandReason] = useState('');
  const [commandExpirationHours, setCommandExpirationHours] = useState(6);
  const [isRevokeConfirmOpen, setIsRevokeConfirmOpen] = useState(false);

  const {
    data: device,
    isLoading: isLoadingDevice,
    error: deviceError,
  } = useQuery({
    queryKey: ['admin-device', deviceId],
    queryFn: () => fetchDeviceById(deviceId!),
    enabled: Boolean(deviceId),
  });

  const { data: commandsData, isLoading: isLoadingCommands } = useQuery({
    queryKey: ['admin-device-commands', deviceId],
    queryFn: () => fetchCommands({ deviceId, limit: 10 }),
    enabled: Boolean(deviceId),
    refetchInterval: 15000, // Poll active device commands
  });

  const commandMutation = useMutation({
    mutationFn: (payload: { type: DeviceCommandType; expiresIn: number; reason: string }) =>
      createDeviceCommand(deviceId!, {
        type: payload.type,
        expires_in: payload.expiresIn,
        reason: payload.reason,
      }),
    onSuccess: (cmd) => {
      success(`Command "${cmd.type}" queued successfully for device.`);
      setIsCommandModalOpen(false);
      setCommandReason('');
      queryClient.invalidateQueries({ queryKey: ['admin-device-commands', deviceId] });
      queryClient.invalidateQueries({ queryKey: ['admin-commands'] });
    },
    onError: (err: any) => {
      toastError(err.message || 'Failed to dispatch command', err.requestId);
    },
  });

  const revokeMutation = useMutation({
    mutationFn: () => revokeDevice(deviceId!),
    onSuccess: () => {
      success(t('devices.revokeSuccessToast'));
      setIsRevokeConfirmOpen(false);
      queryClient.invalidateQueries({ queryKey: ['admin-device', deviceId] });
      queryClient.invalidateQueries({ queryKey: ['admin-devices'] });
    },
    onError: (err: any) => {
      toastError(err.message || 'Failed to revoke device', err.requestId);
    },
  });

  if (isLoadingDevice) {
    return <CardSkeleton />;
  }

  if (deviceError || !device) {
    return (
      <ErrorState
        title={t('devices.loadError')}
        message={deviceError?.message || `Device with ID ${deviceId} does not exist.`}
        onRetry={() => navigate('/admin/devices')}
      />
    );
  }

  const isOnline = Boolean(
    device.last_seen_at && Date.now() / 1000 - device.last_seen_at < 300
  );
  const isRevoked = Boolean(device.revoked_at);

  const handleCreateCommand = (e: React.FormEvent) => {
    e.preventDefault();
    if (!commandReason.trim()) {
      toastError('A clear justification reason is mandatory for all device commands.');
      return;
    }
    commandMutation.mutate({
      type: selectedCommandType,
      expiresIn: commandExpirationHours * 3600,
      reason: commandReason.trim(),
    });
  };

  const timelineEvents = [
    { type: 'Device Registered', time: device.created_at, reqId: 'req_init_9182', status: 'Success' },
    { type: 'Remote Config Fetched', time: device.last_seen_at ? device.last_seen_at - 180 : null, reqId: 'req_cfg_2231', status: 'Delivered' },
    { type: 'Entitlements Verified', time: device.last_seen_at ? device.last_seen_at - 120 : null, reqId: 'req_ent_4419', status: 'Active' },
    ...(commandsData?.items || []).slice(0, 3).map((cmd) => ({
      type: `Command [${cmd.type}] Created`,
      time: cmd.created_at,
      reqId: `req_cmd_${truncateId(cmd.id, 6)}`,
      status: cmd.status.toUpperCase(),
    })),
  ];

  return (
    <div className="space-y-6">
      {/* Top Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Button size="sm" variant="outline" onClick={() => navigate('/admin/devices')}>
            <ArrowLeft className="w-4 h-4 mr-1" /> {t('deviceDetail.backButton')}
          </Button>
          <div>
            <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <span>{device.device_model || 'Device'}</span>
              <DeviceStatusBadge isOnline={isOnline} isRevoked={isRevoked} />
            </h2>
            <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500 font-mono">
              <span>ID:</span>
              <CopyButton value={device.id} truncate={false} />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isRevoked && (
            <>
              <PermissionGuard permission="commands:create">
                <Button
                  size="sm"
                  variant="primary"
                  onClick={() => setIsCommandModalOpen(true)}
                >
                  <Send className="w-3.5 h-3.5 mr-1.5" />
                  {t('deviceDetail.dispatchCommandBtn')}
                </Button>
              </PermissionGuard>

              <PermissionGuard permission="devices:revoke">
                <Button
                  size="sm"
                  variant="danger"
                  onClick={() => setIsRevokeConfirmOpen(true)}
                >
                  <ShieldAlert className="w-3.5 h-3.5 mr-1.5" />
                  {t('devices.revokeButton')}
                </Button>
              </PermissionGuard>
            </>
          )}
        </div>
      </div>

      {/* Grid: Device Info & Telemetry */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('deviceDetail.hardwareSpecs')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-xs">
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">{t('deviceDetail.osVersion')}</span>
              <span className="font-semibold text-slate-900 dark:text-slate-100">
                {device.platform.toUpperCase()} ({device.os_version || 'N/A'})
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">{t('deviceDetail.appVersion')}</span>
              <span className="font-mono font-semibold text-indigo-600 dark:text-indigo-400">
                v{device.app_version}
              </span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">{t('deviceDetail.userId')}</span>
              <button
                onClick={() => navigate(`/admin/users/${device.user_id}`)}
                className="font-mono text-indigo-600 hover:underline"
              >
                {device.user_id}
              </button>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">{t('deviceDetail.registeredAt')}</span>
              <span className="text-slate-900 dark:text-slate-100">{formatDate(device.created_at)}</span>
            </div>
            <div className="flex justify-between py-1.5 border-b border-slate-100 dark:border-slate-800">
              <span className="text-slate-500">{t('deviceDetail.lastSeen')}</span>
              <span className="text-slate-900 dark:text-slate-100">
                {formatDate(device.last_seen_at)} ({formatRelativeTime(device.last_seen_at)})
              </span>
            </div>
            <div className="flex justify-between py-1.5">
              <span className="text-slate-500">{t('deviceDetail.deviceTokenStatus')}</span>
              <span className="font-mono text-slate-400">{device.push_token_hash || 'None'}</span>
            </div>
          </CardContent>
        </Card>

        {/* Mobile <-> Backend Communication Timeline */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Radio className="w-4 h-4 text-emerald-500" />
              Live Mobile ↔ Edge Event Timeline
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="relative border-l-2 border-slate-200 dark:border-slate-800 ml-3 space-y-4 text-xs">
              {timelineEvents.map((evt, idx) => (
                <div key={idx} className="relative pl-5">
                  <div className="absolute -left-[5px] top-1 w-2 h-2 rounded-full bg-indigo-500" />
                  <div className="flex items-center justify-between">
                    <span className="font-semibold text-slate-800 dark:text-slate-200">{evt.type}</span>
                    <span className="text-[10px] font-mono text-slate-400">{evt.reqId}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5 flex items-center justify-between">
                    <span>{formatDate(evt.time)}</span>
                    <span className="font-medium text-emerald-600 dark:text-emerald-400">{evt.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Device Commands History */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Terminal className="w-4 h-4 text-indigo-500" />
            {t('deviceDetail.commandsHistory')}
          </CardTitle>
          <Button size="sm" variant="outline" onClick={() => setIsCommandModalOpen(true)}>
            + {t('deviceDetail.dispatchCommandBtn')}
          </Button>
        </CardHeader>
        <CardContent className="p-0 overflow-x-auto">
          {isLoadingCommands ? (
            <div className="p-4">
              <TableSkeleton rows={3} cols={5} />
            </div>
          ) : !commandsData || commandsData.items.length === 0 ? (
            <div className="p-6 text-center text-xs text-slate-400">
              {t('deviceDetail.noCommands')}
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold uppercase">
                <tr>
                  <th className="py-3 px-4">{t('commands.tableId')}</th>
                  <th className="py-3 px-4">{t('commands.tableType')}</th>
                  <th className="py-3 px-4">{t('commands.tableStatus')}</th>
                  <th className="py-3 px-4">{t('commands.tableCreatedAt')}</th>
                  <th className="py-3 px-4">{t('commands.tableExpiresAt')}</th>
                  <th className="py-3 px-4">{t('commands.tableCreatedBy')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {commandsData.items.map((cmd) => (
                  <tr key={cmd.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                    <td className="py-3 px-4">
                      <CopyButton value={cmd.id} truncate={true} />
                    </td>
                    <td className="py-3 px-4 font-mono font-medium">
                      {t(`commandTypes.${cmd.type}`) !== `commandTypes.${cmd.type}` ? t(`commandTypes.${cmd.type}`) : cmd.type}
                    </td>
                    <td className="py-3 px-4">
                      <CommandStatusBadge status={cmd.status} />
                    </td>
                    <td className="py-3 px-4 text-slate-500">{formatDate(cmd.created_at)}</td>
                    <td className="py-3 px-4 text-slate-500">{formatDate(cmd.expires_at)}</td>
                    <td className="py-3 px-4 font-mono text-slate-500">{cmd.created_by}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Command Dispatch Modal */}
      <Modal
        isOpen={isCommandModalOpen}
        onClose={() => setIsCommandModalOpen(false)}
        title={t('deviceDetail.modalTitle')}
        description={t('commands.description')}
        maxWidth="md"
      >
        <form onSubmit={handleCreateCommand} className="space-y-4 text-xs">
          <div className="space-y-1">
            <label className="font-semibold text-slate-700 dark:text-slate-300">{t('deviceDetail.commandTypeLabel')}</label>
            <select
              value={selectedCommandType}
              onChange={(e) => setSelectedCommandType(e.target.value as DeviceCommandType)}
              className="w-full h-9 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-1 text-xs focus:ring-2 focus:ring-indigo-500"
            >
              {Object.entries(COMMAND_TYPE_LABELS).map(([k, v]) => (
                <option key={k} value={k}>
                  {t(`commandTypes.${k}`) !== `commandTypes.${k}` ? t(`commandTypes.${k}`) : v.label} {v.dangerous ? '⚠️' : ''}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="font-semibold text-slate-700 dark:text-slate-300">
              {t('deviceDetail.commandReasonLabel')} <span className="text-rose-500">*</span>
            </label>
            <textarea
              rows={3}
              value={commandReason}
              onChange={(e) => setCommandReason(e.target.value)}
              placeholder={t('deviceDetail.commandReasonPlaceholder')}
              required
              className="w-full rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 p-2 text-xs focus:ring-2 focus:ring-indigo-500 font-sans"
            />
          </div>

          <div className="space-y-1">
            <label className="font-semibold text-slate-700 dark:text-slate-300">
              {t('deviceDetail.commandTtlLabel')}
            </label>
            <div className="grid grid-cols-4 gap-2">
              {[1, 6, 12, 24].map((hrs) => (
                <button
                  key={hrs}
                  type="button"
                  onClick={() => setCommandExpirationHours(hrs)}
                  className={`py-1.5 px-2 rounded-lg border text-center font-medium transition-colors ${
                    commandExpirationHours === hrs
                      ? 'border-indigo-600 bg-indigo-50 dark:bg-indigo-950/60 text-indigo-600 dark:text-indigo-400 font-bold'
                      : 'border-slate-200 dark:border-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-50'
                  }`}
                >
                  {hrs}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
            <Button
              type="button"
              variant="ghost"
              onClick={() => setIsCommandModalOpen(false)}
              disabled={commandMutation.isPending}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              variant={COMMAND_TYPE_LABELS[selectedCommandType]?.dangerous ? 'danger' : 'primary'}
              isLoading={commandMutation.isPending}
            >
              {t('deviceDetail.submitCommand')}
            </Button>
          </div>
        </form>
      </Modal>

      {/* Revoke Confirmation */}
      <ConfirmDialog
        isOpen={isRevokeConfirmOpen}
        onClose={() => setIsRevokeConfirmOpen(false)}
        onConfirm={() => revokeMutation.mutate()}
        title={t('deviceDetail.revokeConfirmTitle')}
        message={t('devices.revokeModalMessage', { id: deviceId || '' })}
        confirmText={t('devices.revokeConfirmText')}
        variant="danger"
        requireVerificationText="REVOKE"
        isLoading={revokeMutation.isPending}
      />
    </div>
  );
}
